import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Refresh, FileDownload } from '@mui/icons-material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function SalePartyandItemWiseReport() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [PartyandItemWise, setPartyandItemWise] = useState('1');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const { isAuthenticated, companyId, branchId, token, logout, username, companyName } = useAuth();

    const [selectedCustomers, setCustomerType] = useState('0');
    const [selectedItem, setSelectedItem] = useState('0');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (fromDate && toDate && companyId && branchId && token) {
            fetchTableData();
        }
    }, [fromDate, toDate, companyId, branchId, token, PartyandItemWise, selectedCustomers, selectedItem]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const fetchTableData = async () => {
        setDataItem([]);
        
        // Delay showing loading overlay to prevent flash on quick loads
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading sale report...');
        }, 300);
        
        try {
            if (PartyandItemWise === '1') {
                const response = await fetchData('ReportApi', `/SalePartyWise/${companyId}/${selectedCustomers}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } else if (PartyandItemWise === '2') {
                const response = await fetchData('ReportApi', `/SaleItemWise/${companyId}/${selectedItem}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            }
        } catch (error) {
            console.error('Error fetching Sale Report:', error);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const handlesetPartyandItemWiseChange = (event) => {
        setPartyandItemWise(event.target.value);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportCustomerWiseExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Wise Report');
        worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 3 }];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Sale_Customer_Wise_From_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        worksheet.mergeCells('A1:I1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.mergeCells('A2:I2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Sale Customer Wise Report - From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headers = ['Bill Date', 'Bill No', 'Item Name', 'Quantity', 'Rate', 'Taxable', 'GST %', 'GST Amount', 'Total'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        let previousCustomer = null;
        filteredDataItem.forEach((rowData) => {
            if (rowData.MasterName !== previousCustomer) {
                const custRow = worksheet.addRow([`Customer: ${rowData.MasterName}`]);
                custRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe0f2f1' } };
                    cell.alignment = { horizontal: 'left' };
                });
                previousCustomer = rowData.MasterName;
            }

            const dataRow = worksheet.addRow([
                formatDate(rowData.BillDate),
                rowData.BillNumber,
                rowData.ItemName,
                rowData.Quantity,
                rowData.Rate,
                rowData.TaxableAmount,
                `${rowData.GSTRate}%`,
                rowData.GSTAmount,
                rowData.TotalAmount
            ]);

            dataRow.eachCell((cell, colNumber) => {
                cell.border = thinBorder;
                if (colNumber >= 4) {
                    cell.alignment = { horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { horizontal: 'left' };
                }
            });
        });

        worksheet.columns = [
            { width: 15 }, { width: 12 }, { width: 25 }, { width: 10 },
            { width: 10 }, { width: 12 }, { width: 8 }, { width: 12 }, { width: 12 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const handleExportItemWiseExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Item Wise Report');
        worksheet.views = [{ state: 'frozen', ySplit: 3 }];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Sale_Item_Wise_From_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        worksheet.mergeCells('A1:J1');
        const headerCell = worksheet.getCell('A1');
        headerCell.value = companyName || 'Company Name';
        headerCell.font = { bold: true, size: 14 };
        headerCell.alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.mergeCells('A2:J2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Sale Item Wise Report - From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        const columns = ['Item Name', 'Customer', 'Bill Date', 'Bill No', 'Quantity', 'Rate', 'Taxable', 'GST %', 'GST Amount', 'Total'];
        const headerRow = worksheet.addRow(columns);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF667eea' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = thinBorder;
        });

        let previousItem = null;
        filteredDataItem.forEach((rowData) => {
            if (rowData.ItemName !== previousItem) {
                const itemRow = worksheet.addRow([`Item: ${rowData.ItemName}`]);
                itemRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe0f2f1' } };
                    cell.alignment = { horizontal: 'left', vertical: 'center' };
                });
                previousItem = rowData.ItemName;
            }

            const dataRow = worksheet.addRow([
                rowData.ItemName,
                rowData.MasterName,
                formatDate(rowData.BillDate),
                rowData.BillNumber,
                rowData.Quantity,
                rowData.Rate,
                rowData.TaxableAmount,
                `${rowData.GSTRate}%`,
                rowData.GSTAmount,
                rowData.TotalAmount
            ]);

            dataRow.eachCell((cell, colNumber) => {
                cell.border = thinBorder;
                if (colNumber >= 5) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }
            });
        });

        worksheet.columns = [
            { width: 25 }, { width: 25 }, { width: 15 }, { width: 12 },
            { width: 10 }, { width: 10 }, { width: 12 }, { width: 8 },
            { width: 12 }, { width: 12 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    return (
        <div style={{
            minWidth: '100%',
            background: '#f5f7fa',
            padding: isMobile ? '8px' : '10px',
            position: 'relative',
            paddingBottom: isMobile ? '80px' : '10px'
        }}>
            {loading && (
                <Box sx={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', zIndex: '99999 !important', backdropFilter: 'blur(4px)'
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: "#0d766e", mb: 2 }} />
                        <Typography variant="h6" sx={{ color: "#0d766e", fontWeight: 600, fontSize: '1rem' }}>
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{
                maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', padding: isMobile ? '8px' : '12px',
                position: 'relative', color: 'black', fontFamily: "Segoe UI, sans-serif",
            }}>
                <div className="suite-section-heading" style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0',
                    flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ width: '100%' }}>
                            <h4 style={{
                                fontSize: isMobile ? '15px' : '17px', fontWeight: '700', margin: 0,
                                letterSpacing: '-0.3px', fontFamily: "Segoe UI, sans-serif", textTransform: 'uppercase',
                                textAlign: isMobile ? 'center' : 'left'
                            }}>
                                <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif" }}>Sale Party & Item Wise Report</span>
                            </h4>
                        </div>
                    </div>
                </div>

                <div style={{
                    marginBottom: '12px', overflowX: 'auto', overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin'
                }}>
                    <div style={{
                        display: 'flex', gap: '8px', alignItems: 'flex-end', minWidth: 'fit-content',
                        paddingBottom: '4px', flexDirection: isMobile ? 'column' : 'row', width: '100%'
                    }}>
                        <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto', flexWrap: 'nowrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px', flex: isMobile ? '1' : 'none' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                                <input type="date" value={fromDate} onChange={handleFromDateChange}
                                    style={{
                                        width: '100%', borderRadius: '6px', backgroundColor: '#f8f9fa',
                                        fontSize: isMobile ? '10px' : '11px', height: '32px',
                                        border: '1px solid #d1d5db', padding: isMobile ? '8px 6px' : '8px 12px'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px', flex: isMobile ? '1' : 'none' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                                <input type="date" value={toDate} onChange={handleToDateChange}
                                    style={{
                                        width: '100%', borderRadius: '6px', backgroundColor: '#f8f9fa',
                                        fontSize: isMobile ? '10px' : '11px', height: '32px',
                                        border: '1px solid #d1d5db', padding: isMobile ? '8px 6px' : '8px 12px'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{
                            display: 'flex', gap: '8px', alignItems: 'center',
                            width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start'
                        }}>
                            <FormControl component="fieldset" style={{ marginLeft: isMobile ? '20px' : '8px' }}>
                                <RadioGroup row value={PartyandItemWise} onChange={handlesetPartyandItemWiseChange} style={{ gap: '8px' }}>
                                    <FormControlLabel value="1"
                                        control={<Radio size="small" sx={{ padding: '4px', '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                                        label={<span style={{ fontSize: '11px', color: '#374151' }}>Party Wise</span>}
                                    />
                                    <FormControlLabel value="2"
                                        control={<Radio size="small" sx={{ padding: '4px', '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                                        label={<span style={{ fontSize: '11px', color: '#374151' }}>Item Wise</span>}
                                    />
                                </RadioGroup>
                            </FormControl>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                {isMobile ? (
                                    <>
                                        <Tooltip title="Refresh">
                                            <IconButton onClick={fetchTableData}
                                                style={{
                                                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                                    color: 'white', width: '32px', height: '32px',
                                                    boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)", padding: '4px'
                                                }}>
                                                <Refresh fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Export">
                                            <IconButton
                                                onClick={PartyandItemWise === '1' ? handleExportCustomerWiseExcel : handleExportItemWiseExcel}
                                                style={{
                                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                    color: 'white', width: '32px', height: '32px',
                                                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)', padding: '4px'
                                                }}>
                                                <FileDownload fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>
                                        <Button onClick={fetchTableData}
                                            style={{
                                                background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                                color: 'white', padding: '4px 12px', borderRadius: '6px',
                                                fontSize: '12px', fontWeight: '700', boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                                                border: 'none', height: '32px', textTransform: 'none', letterSpacing: '0.5px', minWidth: '100px'
                                            }}>
                                            Refresh
                                        </Button>
                                        <Button
                                            onClick={PartyandItemWise === '1' ? handleExportCustomerWiseExcel : handleExportItemWiseExcel}
                                            style={{
                                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                color: 'white', padding: '4px 12px', borderRadius: '6px',
                                                fontSize: '12px', fontWeight: '700', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                                                border: 'none', height: '32px', textTransform: 'none', letterSpacing: '0.5px', minWidth: '100px'
                                            }}>
                                            Export
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {PartyandItemWise === '1' && (
                    <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                        <TableContainer component={Paper} style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)', borderRadius: '6px', overflow: isMobile ? 'auto' : 'hidden' }}>
                            <Table sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                                <TableHead>
                                    <TableRow style={{ background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)", height: '24px' }}>
                                        {['Bill Date', 'Bill Number', 'Item Name', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'GST Amount', 'Total Amount'].map((col) => (
                                            <TableCell key={col} style={{
                                                color: 'white', fontWeight: '600', fontSize: isMobile ? '8px' : '9px',
                                                textTransform: 'uppercase', letterSpacing: '0.2px', padding: isMobile ? '2px 4px' : '2px 6px',
                                                textAlign: ['Bill Date', 'Bill Number', 'Item Name'].includes(col) ? 'left' : 'right', height: '24px'
                                            }}>
                                                {col}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(filteredDataItem.reduce((acc, item) => {
                                        if (!acc[item.MasterName]) acc[item.MasterName] = [];
                                        acc[item.MasterName].push(item);
                                        return acc;
                                    }, {})).map(([customerName, items], customerIndex) => {
                                        const subtotalQty = items.reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
                                        const subtotalTaxableAmount = items.reduce((sum, item) => sum + Number(item.TaxableAmount || 0), 0);
                                        const subtotalGSTAmount = items.reduce((sum, item) => sum + Number(item.GSTAmount || 0), 0);
                                        const subtotalTotalAmount = items.reduce((sum, item) => sum + Number(item.TotalAmount || 0), 0);

                                        return (
                                            <React.Fragment key={customerIndex}>
                                                <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                                                    <TableCell colSpan={9} sx={{ fontWeight: 'bold', fontSize: isMobile ? '10px' : '12px', color: '#004d40', padding: isMobile ? '4px' : '6px 8px' }}>
                                                        {customerName}
                                                    </TableCell>
                                                </TableRow>
                                                {items.map((data, index) => (
                                                    <TableRow key={index} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }, '&:hover': { backgroundColor: '#f1f5f9', transition: 'all 0.15s ease-in-out' }, borderBottom: '1px solid #f1f5f9', height: '28px' }}>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                                {formatDate(data.BillDate)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.BillNumber}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.ItemName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.Quantity}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.Rate.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.TaxableAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.GSTRate}%
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.GSTAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#1e40af' }}>
                                                                ₹{data.TotalAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: "#0d766e" }}>
                                                    <TableCell colSpan={3} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>Subtotal</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>{subtotalQty}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }} />
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalTaxableAmount.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }} />
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalGSTAmount.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalTotalAmount.toFixed(2)}</TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                )}

                {PartyandItemWise === '2' && (
                    <div style={{ marginBottom: isMobile ? '80px' : '8px' }}>
                        <TableContainer component={Paper} style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)', borderRadius: '6px', overflow: isMobile ? 'auto' : 'hidden' }}>
                            <Table sx={{ minWidth: isMobile ? 900 : 650 }} size="small">
                                <TableHead>
                                    <TableRow style={{ background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)", height: '24px' }}>
                                        {['Item Name', 'Customer', 'Bill Date', 'Bill Number', 'Quantity', 'Rate', 'Taxable Amount', 'GST Rate', 'GST Amount', 'Total Amount'].map((col) => (
                                            <TableCell key={col} style={{
                                                color: 'white', fontWeight: '600', fontSize: isMobile ? '8px' : '9px',
                                                textTransform: 'uppercase', letterSpacing: '0.2px', padding: isMobile ? '2px 4px' : '2px 6px',
                                                textAlign: ['Item Name', 'Customer', 'Bill Date', 'Bill Number'].includes(col) ? 'left' : 'right', height: '24px'
                                            }}>
                                                {col}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(filteredDataItem.reduce((acc, item) => {
                                        if (!acc[item.ItemName]) acc[item.ItemName] = [];
                                        acc[item.ItemName].push(item);
                                        return acc;
                                    }, {})).map(([itemName, customers], itemIndex) => {
                                        const subtotalQty = customers.reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
                                        const subtotalTaxableAmount = customers.reduce((sum, item) => sum + Number(item.TaxableAmount || 0), 0);
                                        const subtotalGSTAmount = customers.reduce((sum, item) => sum + Number(item.GSTAmount || 0), 0);
                                        const subtotalTotalAmount = customers.reduce((sum, item) => sum + Number(item.TotalAmount || 0), 0);

                                        return (
                                            <React.Fragment key={itemIndex}>
                                                <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                                                    <TableCell colSpan={10} sx={{ fontWeight: 'bold', fontSize: isMobile ? '10px' : '12px', color: '#004d40', padding: isMobile ? '4px' : '6px 8px' }}>
                                                        {itemName}
                                                    </TableCell>
                                                </TableRow>
                                                {customers.map((data, index) => (
                                                    <TableRow key={index} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafbfc' }, '&:hover': { backgroundColor: '#f1f5f9', transition: 'all 0.15s ease-in-out' }, borderBottom: '1px solid #f1f5f9', height: '28px' }}>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.ItemName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.MasterName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                                {formatDate(data.BillDate)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75 }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#374151' }}>
                                                                {data.BillNumber}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.Quantity}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.Rate.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.TaxableAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                {data.GSTRate}%
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 600, color: '#374151' }}>
                                                                ₹{data.GSTAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, px: isMobile ? 0.5 : 0.75, textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', fontWeight: 700, color: '#1e40af' }}>
                                                                ₹{data.TotalAmount.toFixed(2)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: "#0d766e" }}>
                                                    <TableCell colSpan={4} sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>Subtotal</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>{subtotalQty}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }} />
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalTaxableAmount.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }} />
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalGSTAmount.toFixed(2)}</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', padding: '3px', color: 'white' }}>₹{subtotalTotalAmount.toFixed(2)}</TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SalePartyandItemWiseReport;
